/**
 * NOTE: This component is being refactored to use shared components from Components/
 * The table uses:
 * - @frontend/Components/StatusBadge/StatusBadge (for status display) ✓
 * - @frontend/Components/Table/Table (already using)
 * - @frontend/Components/ProgressBar/ProgressBar (for budget health - TODO)
 *
 * Following the Component Consolidation Migration Plan.
 */

import {
  faSort,
  faSortUp,
  faSortDown,
  faBriefcase,
  faGavel,
  faFileAlt,
  faBuilding,
  faEllipsisVertical,
  faEdit,
  faTrash,
  faSave,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Table, { getDensityClass } from "../../../Components/Table/Table";
import StatusBadge from "../../../Components/StatusBadge/StatusBadge";
import ProgressBar from "../../../Components/ProgressBar/ProgressBar";
import MatterService from "../../../AppSettings/services/MatterService";
import MilestoneService, {
  NoMilestoneError,
} from "../../../AppSettings/services/MilestoneService";
import ToastService from "../../../AppSettings/services/ToastService";
import MatterLink from "./Components/MatterLink";
import ConfirmationModal from "../../../Components/ConfirmationModal/ConfirmationModal";
import { useMemo, useState, useEffect, useRef } from "react";
import { isEngineeringEnvironment } from "../../../AppSettings/environmentConfig";
import type { PaginationProps } from "@frontend/Models/Table/TableModels";

export interface MatterRow {
  matterid: string;
  mattername?: string;
  mattertype?: string;
  country?: string;
  status?: string;
  closedate?: string;
  due_date?: string;
  clientname?: string;
  company?: string;
  Company?: string;
  agentname?: string;
  employeename?: string;
  employee_title?: string;
  employeeTitle?: string;
  employee_avatar?: string;
  employeeAvatar?: string;
  application_no?: string;
  amount_spent?: number | string;
  spent?: number | string;
  budget?: number | string;
  amount?: number | string;
  milestoneCount?: number | string;
  milestone_count?: number | string;
  completedCount?: number | string;
  completed_count?: number | string;
  progress?: number;
  client_type?: string;
  clientType?: string;
  opendate?: string;
}

interface EditData {
  mattername?: string;
  status?: string;
  closedate?: string;
  clientname?: string;
}

// Milestone/aggregate dates are calendar dates; drop any time component so
// comparisons and payloads stay in YYYY-MM-DD form.
const toDateOnly = (value?: string | null): string => {
  if (!value) return "";
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : value.slice(0, 10);
};

// Parse a due date for display. Calendar-only strings (YYYY-MM-DD) are built as
// LOCAL dates; `new Date("YYYY-MM-DD")` would parse them as UTC midnight and
// render as the previous day in negative UTC offsets (e.g. 2027-01-01 -> Dec 31).
const parseLocalDate = (value: string): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
};

type SortKey =
  | "matter"
  | "status"
  | "client"
  | "leader"
  | "budget"
  | "duedate"
  | "opendate"
  | "progress"
  | "jobnumber";
type SortDir = "asc" | "desc" | null;

interface MattersTableProps {
  data: MatterRow[] | null;
  showLeader?: boolean;
  onMatterRefresh?: () => void;
  pageSize?: number;
  pagination?: PaginationProps;
}

const STATUS_OPTIONS = [
  { value: "Prospective", label: "Prospective", color: "#9e9e9e" },
  { value: "Not Started", label: "Not Started", color: "#6c757d" },
  { value: "In progress", label: "In progress", color: "#17a2b8" },
  { value: "Redline", label: "Redline", color: "#ff9800" },
  { value: "On Hold", label: "On Hold", color: "#ffc107" },
  { value: "Complete", label: "Complete", color: "#28a745" },
  { value: "Cancelled", label: "Cancelled", color: "#dc3545" },
] as const;

const MattersTable = ({
  data,
  showLeader = false,
  onMatterRefresh,
  pageSize = 7,
  pagination,
}: MattersTableProps): JSX.Element => {
  const [sortBy, setSortBy] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [matterToDelete, setMatterToDelete] = useState<string | null>(null);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editData, setEditData] = useState<EditData>({});
  const [isSaving, setIsSaving] = useState(false);
  const menuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const getText = (legalText: string, engText: string): string => {
    return isEngineeringEnvironment ? engText : legalText;
  };

  const getStatusColor = (status: string | undefined): string => {
    if (!status) return "#9e9e9e";
    const option = STATUS_OPTIONS.find(
      (opt) => opt.value.toLowerCase() === status.toLowerCase()
    );
    return option ? option.color : "#9e9e9e";
  };

  const getStatusVariant = (
    status: string | undefined
  ): "open" | "completed" | "at-risk" | "in-review" | "pending" | "active" => {
    if (!status) return "open";
    const statusLower = status.toLowerCase();

    if (statusLower.includes("complete") || statusLower === "complete") {
      return "completed";
    } else if (statusLower.includes("cancelled") || statusLower === "cancelled") {
      return "at-risk";
    } else if (
      statusLower.includes("progress") ||
      statusLower.includes("in progress")
    ) {
      return "in-review";
    } else if (statusLower.includes("hold") || statusLower === "on hold") {
      return "pending";
    } else if (statusLower.includes("redline") || statusLower === "redline") {
      return "in-review";
    } else if (
      statusLower.includes("prospective") ||
      statusLower === "prospective"
    ) {
      return "open";
    } else if (
      statusLower.includes("not started") ||
      statusLower === "not started"
    ) {
      return "open";
    }

    if (statusLower.includes("active") || statusLower.includes("open")) {
      return "active";
    } else if (statusLower.includes("risk") || statusLower.includes("overdue")) {
      return "at-risk";
    } else if (statusLower.includes("review") || statusLower.includes("progress")) {
      return "in-review";
    }

    return "open";
  };

  const toggleSort = (key: SortKey): void => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir("asc");
      return;
    }
    setSortDir((prev) =>
      prev === "asc" ? "desc" : prev === "desc" ? null : "asc"
    );
  };

  const getSortValue = (key: SortKey, row: MatterRow): string | number => {
    switch (key) {
      case "matter":
        return row.matterid || "";
      case "status":
        return row.status || "";
      case "client":
        return (
          row.clientname || row.company || row.Company || row.agentname || ""
        );
      case "leader":
        return row.employeename || "";
      case "budget":
        return (
          parseFloat(String(row.amount_spent || 0)) /
          parseFloat(String(row.budget || row.amount || 1))
        );
      case "jobnumber":
        return row.application_no || "";
      case "duedate":
        return row.closedate || row.due_date || "";
      case "opendate":
        return row.opendate || "";
      default:
        return "";
    }
  };

  const getMatterTypeIcon = (matterType: string | undefined): IconDefinition => {
    if (!matterType) return faBriefcase;
    const typeLower = matterType.toLowerCase();
    if (typeLower.includes("corporate") || typeLower.includes("business"))
      return faBriefcase;
    if (
      typeLower.includes("legal") ||
      typeLower.includes("dispute") ||
      typeLower.includes("litigation")
    )
      return faGavel;
    if (
      typeLower.includes("real estate") ||
      typeLower.includes("property")
    )
      return faBuilding;
    return faFileAlt;
  };

  const getMatterTypeColor = (matterType: string | undefined): string => {
    if (!matterType) return "#6f42c1";
    const typeLower = matterType.toLowerCase();
    if (typeLower.includes("corporate") || typeLower.includes("business"))
      return "#6f42c1";
    if (
      typeLower.includes("legal") ||
      typeLower.includes("dispute") ||
      typeLower.includes("litigation")
    )
      return "#fd7e14";
    if (
      typeLower.includes("real estate") ||
      typeLower.includes("property")
    )
      return "#6f42c1";
    return "#1a1a2e";
  };

  const formatDueDate = (
    dateString: string | undefined
  ): { date: string; status: string; color: string } => {
    if (!dateString) return { date: "N/A", status: "", color: "#6c757d" };

    const dueDate = parseLocalDate(dateString);
    if (isNaN(dueDate.getTime())) return { date: "N/A", status: "", color: "#6c757d" };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const formattedDate = dueDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    if (diffDays < 0) {
      return {
        date: formattedDate,
        status: `${Math.abs(diffDays)} days overdue`,
        color: "#dc3545",
      };
    } else if (diffDays === 0) {
      return {
        date: formattedDate,
        status: "Due today",
        color: "#ffc107",
      };
    } else if (diffDays <= 7) {
      return {
        date: formattedDate,
        status: `${diffDays} days left`,
        color: "#ffc107",
      };
    } else {
      return {
        date: formattedDate,
        status: `${diffDays} days left`,
        color: "#6c757d",
      };
    }
  };

  const isCompleted = (row: MatterRow): boolean => {
    return !!(
      row.status &&
      (row.status.toLowerCase().includes("complete") ||
        row.status.toLowerCase().includes("closed"))
    );
  };

  const displayedData = useMemo(() => {
    if (!data || !sortBy || !sortDir) return data;
    const comparator = (a: MatterRow, b: MatterRow) => {
      const va = getSortValue(sortBy, a);
      const vb = getSortValue(sortBy, b);

      if (sortBy === "opendate") {
        const da = Date.parse(String(va));
        const db = Date.parse(String(vb));
        if (!Number.isNaN(da) && !Number.isNaN(db)) {
          return da - db;
        }
      }
      return String(va).localeCompare(String(vb), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    };
    const sorted = [...data].sort(comparator);
    return sortDir === "asc" ? sorted : sorted.reverse();
  }, [data, sortBy, sortDir]);

  const SortHeader = ({
    label,
    colKey,
  }: {
    label: string;
    colKey: SortKey;
  }) => (
    <div
      onClick={() => toggleSort(colKey)}
      style={{
        cursor: "pointer",
        userSelect: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span>{label}</span>
      <span>
        {sortBy === colKey && sortDir === "asc" && (
          <FontAwesomeIcon icon={faSortUp} />
        )}
        {sortBy === colKey && sortDir === "desc" && (
          <FontAwesomeIcon icon={faSortDown} />
        )}
        {(sortBy !== colKey || sortDir === null) && (
          <FontAwesomeIcon icon={faSort} />
        )}
      </span>
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (openMenuId) {
        const menuRef = menuRefs.current[openMenuId];
        const target = event.target as Node;
        if (menuRef && !menuRef.contains(target)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const toggleMenu = (matterId: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === matterId ? null : matterId);
  };

  const handleEdit = (matterId: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    setOpenMenuId(null);
    const row = data?.find((r) => r.matterid === matterId);
    if (row) {
      setEditingRowId(matterId);
      setEditData({
        mattername: row.mattername || "",
        status: row.status || "",
        closedate: row.closedate || row.due_date || "",
        clientname: row.clientname || "",
      });
    }
  };

  const handleCancelEdit = (): void => {
    setEditingRowId(null);
    setEditData({});
  };

  const handleDueDateClick = (matterId: string): void => {
    setOpenMenuId(null);
    const row = data?.find((r) => r.matterid === matterId);
    if (row) {
      setEditingRowId(matterId);
      setEditData({
        mattername: row.mattername || "",
        status: row.status || "",
        closedate: row.closedate || row.due_date || "",
        clientname: row.clientname || "",
      });
    }
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!editingRowId) return;

    setIsSaving(true);
    try {
      if (isEngineeringEnvironment) {
        // Projects don't have a stored due date; route the due date edit to the
        // schedule's final milestone so it actually persists and re-renders.
        // Name and status persist through MatterService.updateMatter, matching
        // the project detail TopBar. If nothing persistable changed, just close
        // the editor without a pointless write.
        const originalRow = data?.find((r) => r.matterid === editingRowId);
        const originalDate = toDateOnly(originalRow?.closedate || originalRow?.due_date);
        const newDate = toDateOnly(editData.closedate);
        const dueDateChanged = Boolean(newDate) && newDate !== originalDate;

        const originalStatus = originalRow?.status || "";
        const newStatus = editData.status ?? originalStatus;
        // Treat the blank "Select Status" placeholder as "no change" so a
        // stray selection can't wipe an existing status (matches TopBar).
        const statusChanged = Boolean(newStatus) && newStatus !== originalStatus;

        const originalName = originalRow?.mattername || "";
        const draftedName = editData.mattername ?? originalName;
        const trimmedName = draftedName.trim();
        const nameChanged = Boolean(trimmedName) && trimmedName !== originalName.trim();

        if (!dueDateChanged && !statusChanged && !nameChanged) {
          setEditingRowId(null);
          setEditData({});
          return;
        }

        if (nameChanged || statusChanged) {
          const updatePayload: { mattername?: string; status?: string } = {};
          if (nameChanged) updatePayload.mattername = trimmedName;
          if (statusChanged) updatePayload.status = newStatus;
          await MatterService.updateMatter(editingRowId, updatePayload);
        }

        if (dueDateChanged) {
          await MilestoneService.setProjectDueDate(editingRowId, newDate);
        }
      } else {
        const updatePayload = {
          mattername: editData.mattername,
          status: editData.status,
          closedate: editData.closedate || undefined,
        };

        await MatterService.updateMatter(editingRowId, updatePayload);
      }

      ToastService.success(
        getText("Matter updated successfully", "Project updated successfully")
      );

      setEditingRowId(null);
      setEditData({});

      if (onMatterRefresh) {
        await onMatterRefresh();
      }
    } catch (error) {
      console.error(
        getText("Error updating matter:", "Error updating project:"),
        error
      );
      if (error instanceof NoMilestoneError) {
        ToastService.error(
          "Add a milestone to this project before setting a due date."
        );
      } else {
        ToastService.error(
          getText(
            "Failed to update matter. Please try again.",
            "Failed to update project. Please try again."
          )
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: keyof EditData, value: string): void => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDeleteClick = (matterId: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    setOpenMenuId(null);
    setMatterToDelete(matterId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!matterToDelete) return;

    try {
      let result:
        | { error?: string }
        | Awaited<ReturnType<typeof MatterService.deleteMatter>>
        | Awaited<ReturnType<typeof MatterService.deleteProjectMatterById>>;
      if (isEngineeringEnvironment) {
        result = await MatterService.deleteProjectMatterById(matterToDelete);
      } else {
        result = await MatterService.deleteMatter(matterToDelete);
      }

      if (result && "error" in result && result.error) {
        throw new Error(result.error);
      }

      setDeleteModalOpen(false);
      setMatterToDelete(null);

      ToastService.success(
        getText("Matter deleted successfully", "Project deleted successfully")
      );

      if (onMatterRefresh) {
        await onMatterRefresh();
      }
    } catch (error) {
      console.error(
        getText("Error deleting matter:", "Error deleting project:"),
        error
      );
      ToastService.error(
        getText(
          "Failed to delete matter. Please try again.",
          "Failed to delete project. Please try again."
        )
      );
    }
  };

  const handleDeleteCancel = (): void => {
    setDeleteModalOpen(false);
    setMatterToDelete(null);
  };

  if (!data) {
    return <></>;
  }

  const columns = [
    {
      header: (
        <SortHeader
          label={getText("Matter Name", "Project Name")}
          colKey={"matter"}
        />
      ),
      accessor: "matterid",
      render: (row: MatterRow) => {
        const isEditing = editingRowId === row.matterid;
        const matterType = row.mattertype || "";
        const icon = getMatterTypeIcon(matterType);
        const iconColor = getMatterTypeColor(matterType);
        const matterName = isEditing
          ? (editData.mattername !== undefined
              ? editData.mattername
              : row.mattername || "")
          : row.mattername || getText("Untitled Matter", "Untitled Project");

        return (
          <div className="matter-name-cell">
            <div
              className="matter-icon-wrapper"
              style={{ backgroundColor: iconColor }}
            >
              <FontAwesomeIcon icon={icon} className="matter-type-icon" />
            </div>
            <div className="matter-name-content">
              {isEditing ? (
                <input
                  type="text"
                  value={matterName}
                  onChange={(e) =>
                    handleFieldChange("mattername", e.target.value)
                  }
                  className="inline-edit-input"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handleSaveEdit();
                    } else if (e.key === "Escape") {
                      handleCancelEdit();
                    }
                  }}
                />
              ) : (
                <>
                  <div className="matter-name-text">
                    <MatterLink matterid={row.matterid} mattername={matterName} />
                  </div>
                  <div className="matter-id-text">
                    {row.matterid || "N/A"}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      },
    },
    {
      header: (
        <SortHeader label={getText("Application ID", "Job Number")} colKey={"jobnumber"} />
      ),
      accessor: "application_no",
      render: (row: MatterRow) => {
        return (
          <span className="application-id-cell">
            {row.application_no || <span className="table-na">N/A</span>}
          </span>
        );
      },
    },
    {
      header: (
        <SortHeader label={"Client"} colKey={"client"} />
      ),
      accessor: "clientname",
      render: (row: MatterRow) => {
        const isEditing = editingRowId === row.matterid;
        const clientDisplay =
          row.clientname || row.company || row.Company || row.agentname || "";
        const clientName = isEditing
          ? (editData.clientname !== undefined
              ? editData.clientname
              : clientDisplay || "")
          : clientDisplay || "N/A";
        const clientType = row.client_type || row.clientType || "";

        return (
          <div className="client-cell">
            {isEditing ? (
              <input
                type="text"
                value={clientName}
                onChange={(e) =>
                  handleFieldChange("clientname", e.target.value)
                }
                className="inline-edit-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void handleSaveEdit();
                  } else if (e.key === "Escape") {
                    handleCancelEdit();
                  }
                }}
              />
            ) : (
              <>
                <div className="client-name">{clientName}</div>
                {clientType && (
                  <div className="client-type">{clientType}</div>
                )}
              </>
            )}
          </div>
        );
      },
    },
    ...(showLeader
      ? [
          {
            header: (
              <SortHeader label={"Leader"} colKey={"leader"} />
            ),
            accessor: "employeename",
            render: (row: MatterRow) => {
              const leaderName = row.employeename || "Unassigned";
              const leaderTitle =
                row.employee_title || row.employeeTitle || "Team Member";
              const avatarUrl = row.employee_avatar || row.employeeAvatar;

              return (
                <div className="leader-cell">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={leaderName}
                      className="leader-avatar"
                    />
                  ) : (
                    <div className="leader-avatar-placeholder">
                      {leaderName
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .substring(0, 2)
                        .toUpperCase()}
                    </div>
                  )}
                  <div className="leader-info">
                    <div className="leader-name">{leaderName}</div>
                    <div className="leader-title">{leaderTitle}</div>
                  </div>
                </div>
              );
            },
          },
        ]
      : []),
    {
      header: (
        <SortHeader label={"Status"} colKey={"status"} />
      ),
      accessor: "status",
      render: (row: MatterRow) => {
        const isEditing = editingRowId === row.matterid;
        const statusValue = isEditing
          ? (editData.status !== undefined ? editData.status : row.status || "")
          : row.status || "";

        if (isEditing) {
          return (
            <select
              value={statusValue}
              onChange={(e) => handleFieldChange("status", e.target.value)}
              className="inline-edit-select status-select"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleSaveEdit();
                } else if (e.key === "Escape") {
                  handleCancelEdit();
                }
              }}
            >
              <option value="">Select Status</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );
        }

        if (!row.status) return <span className="table-na">N/A</span>;

        const statusColor = getStatusColor(row.status);
        const variant = getStatusVariant(row.status);

        return (
          <StatusBadge
            status={row.status}
            variant={variant}
            color={statusColor}
            size="medium"
          />
        );
      },
    },
    {
      header: (
        <SortHeader label={"Due Date"} colKey={"duedate"} />
      ),
      accessor: "closedate",
      render: (row: MatterRow) => {
        const isEditing = editingRowId === row.matterid;
        const dateValue = isEditing
          ? (editData.closedate !== undefined
              ? editData.closedate
              : row.closedate || row.due_date || "")
          : row.closedate || row.due_date || "";

        if (isEditing) {
          return (
            <input
              type="date"
              value={toDateOnly(dateValue)}
              onChange={(e) =>
                handleFieldChange("closedate", e.target.value)
              }
              className="inline-edit-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleSaveEdit();
                } else if (e.key === "Escape") {
                  handleCancelEdit();
                }
              }}
            />
          );
        }

        const dueDateInfo = formatDueDate(row.closedate || row.due_date);
        const completed = isCompleted(row);

        return (
          <div
            className="due-date-cell due-date-cell-clickable"
            onClick={(e) => {
              e.stopPropagation();
              handleDueDateClick(row.matterid);
            }}
            title={getText("Click to edit due date", "Click to edit due date")}
          >
            <div
              className="due-date-text"
              style={{ color: completed ? "#28a745" : dueDateInfo.color }}
            >
              {dueDateInfo.date}
            </div>
            {completed ? (
              <div className="due-date-status" style={{ color: "#28a745" }}>
                Completed
              </div>
            ) : (
              <div
                className="due-date-status"
                style={{ color: dueDateInfo.color }}
              >
                {dueDateInfo.status}
              </div>
            )}
          </div>
        );
      },
    },
    ...(!isEngineeringEnvironment
      ? [
          {
            header: (
              <SortHeader label={"Budget Health"} colKey={"budget"} />
            ),
            accessor: "budget",
            render: (row: MatterRow) => {
              const current = parseFloat(String(row.amount_spent || row.spent || 0));
              const total = parseFloat(String(row.budget || row.amount || 0));

              if (total === 0) {
                return <span className="table-na">No budget set</span>;
              }

              return (
                <div className="budget-health-cell">
                  <ProgressBar
                    current={current}
                    total={total}
                    displayFormat="amount"
                    size="small"
                  />
                </div>
              );
            },
          },
        ]
      : [
          {
            header: (
              <SortHeader
                label={getText("Progress", "Progress")}
                colKey={"progress"}
              />
            ),
            accessor: "progress",
            render: (row: MatterRow) => {
              let progress = 0;
              const milestoneCountRaw =
                row.milestoneCount ?? row.milestone_count ?? 0;
              const milestoneCount =
                typeof milestoneCountRaw === "number"
                  ? milestoneCountRaw
                  : parseInt(String(milestoneCountRaw), 10) || 0;

              const completedCountRaw =
                row.completedCount ?? row.completed_count ?? 0;
              const completedCount =
                typeof completedCountRaw === "number"
                  ? completedCountRaw
                  : parseInt(String(completedCountRaw), 10) || 0;

              const rowProgress =
                row.progress !== undefined && row.progress !== null
                  ? parseFloat(String(row.progress))
                  : null;

              // A8: show the completed/total model on the list so it AGREES with the schedule tooltip
              // (both derive from the backend aggregate `progress = round(completed/total*100)`). Prefer
              // the backend-computed row.progress — identical to the tooltip's source — and fall back to
              // completed/total from the row's own counts when the aggregate isn't supplied (e.g. legal).
              // Was a step-index formula min(completed, n-1)/(n-1), which rendered 1-of-3 as 50% ≠ 33%.
              if (rowProgress !== null && !isNaN(rowProgress) && rowProgress >= 0) {
                progress = rowProgress;
              } else if (milestoneCount > 0) {
                progress = Math.round((completedCount / milestoneCount) * 100);
              } else {
                progress = 0;
              }

              const progressPercent = Math.min(100, Math.max(0, progress));

              return (
                <div className="budget-health-cell">
                  <div className="progress-cell-content">
                    <div className="progress-steps-info">
                      {milestoneCount} steps • {Math.round(progressPercent)}%
                      complete
                    </div>
                    <div className="overall-progress-bar">
                      <div
                        className="overall-progress-background"
                        style={{ backgroundColor: "#e0e0e0" }}
                      >
                        <div
                          className="overall-progress-fill"
                          style={{
                            width: `${progressPercent}%`,
                            backgroundColor: "#4caf50",
                          }}
                        />
                      </div>
                      <span className="overall-progress-text">
                        {Math.round(progressPercent)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            },
          },
        ]),
    {
      header: (
        <div className="table-action-header">
          <span className="table-action-header-text">Actions</span>
        </div>
      ),
      render: (row: MatterRow) => {
        const isEditing = editingRowId === row.matterid;
        const isMenuOpen = openMenuId === row.matterid;

        if (isEditing) {
          return (
            <div className="table-action-cell table-action-edit-buttons">
              <button
                className="table-action-save-button"
                onClick={() => void handleSaveEdit()}
                disabled={isSaving}
                aria-label={getText("Save changes", "Save changes")}
                title={getText("Save changes", "Save changes")}
              >
                <FontAwesomeIcon icon={faSave} className="table-action-button-icon" />
                <span className="table-action-button-text">
                  {isSaving
                    ? getText("Saving...", "Saving...")
                    : getText("Save", "Save")}
                </span>
              </button>
              <button
                className="table-action-cancel-button"
                onClick={handleCancelEdit}
                disabled={isSaving}
                aria-label={getText("Cancel editing", "Cancel editing")}
                title={getText("Cancel editing", "Cancel editing")}
              >
                <FontAwesomeIcon icon={faTimes} className="table-action-button-icon" />
                <span className="table-action-button-text">
                  {getText("Cancel", "Cancel")}
                </span>
              </button>
            </div>
          );
        }

        return (
          <div
            className="table-action-cell"
            ref={(el) => {
              if (el) menuRefs.current[row.matterid] = el;
            }}
          >
            <button
              className="table-action-button"
              data-testid={`matter-actions-${row.matterid}`}
              aria-label={getText(
                `Actions for ${row.matterid}`,
                `Actions for ${row.matterid}`
              )}
              aria-expanded={isMenuOpen}
              onClick={(e) => toggleMenu(row.matterid, e)}
            >
              <FontAwesomeIcon icon={faEllipsisVertical} />
            </button>
            {isMenuOpen && (
              <div className="table-action-dropdown">
                <button
                  className="table-action-dropdown-item"
                  onClick={(e) => handleEdit(row.matterid, e)}
                  aria-label={getText("Edit matter", "Edit project")}
                >
                  <FontAwesomeIcon icon={faEdit} className="table-action-dropdown-icon" />
                  <span>{getText("Edit", "Edit")}</span>
                </button>
                <button
                  className="table-action-dropdown-item table-action-dropdown-item-danger"
                  data-testid={`matter-delete-${row.matterid}`}
                  onClick={(e) => handleDeleteClick(row.matterid, e)}
                  aria-label={getText("Delete matter", "Delete project")}
                >
                  <FontAwesomeIcon icon={faTrash} className="table-action-dropdown-icon" />
                  <span>{getText("Delete", "Delete")}</span>
                </button>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className={getDensityClass(pageSize)}>
        <Table
          columns={columns}
          data={displayedData}
          emptyMessage={getText("No matters found", "No projects found")}
          rowKey="matterid"
          pagination={pagination}
        />
      </div>
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={() => void handleDeleteConfirm()}
        message={getText(
          "Are you sure you want to delete this matter? This action cannot be undone.",
          "Are you sure you want to delete this project? This action cannot be undone."
        )}
        confirmLabel={getText("Delete", "Delete")}
        cancelLabel={getText("Cancel", "Cancel")}
      />
    </>
  );
};

export default MattersTable;
